import React from 'react';
import { emissionsParts, fmtShare, usePalette } from '../charts/chartKit';

/**
 * KPI row. Category identity is a small colour mark + its name, never a full-colour fill.
 * Values use thousands separators and switch to tonnes from 1,000 kg.
 *
 * GLASS = true renders the iOS "Liquid Glass" style tiles (translucent material over a soft
 * category-colour light field, specular rim, pointer-tracked highlight). Set it to false to go
 * back to the flat card tiles; the matching CSS lives in index.css under "LIQUID GLASS TILES".
 */
const GLASS = true;

// Move the specular highlight with the pointer (1:1, no animation) — light catching the material.
function trackLight(e) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--lg-x', `${((e.clientX - r.left) / r.width) * 100}%`);
  e.currentTarget.style.setProperty('--lg-y', `${((e.clientY - r.top) / r.height) * 100}%`);
}
function resetLight(e) {
  e.currentTarget.style.removeProperty('--lg-x');
  e.currentTarget.style.removeProperty('--lg-y');
}

export default function MetricsGrid({ totals, scope3Summary }) {
  const palette = usePalette();
  const cat4 = Number(totals?.upstreamEmissions || 0);
  const cat9 = Number(totals?.downstreamEmissions || 0);
  const cat6 = Number(scope3Summary?.by_category?.[6] || 0);
  const cat7 = Number(scope3Summary?.by_category?.[7] || 0);
  const total = (totals?.totalEmissions || 0) + cat6 + cat7;
  const totalParts = emissionsParts(total);

  const tiles = [
    { cat: 4, label: 'Cat 4 · Upstream', note: 'Freight in', value: cat4 },
    { cat: 9, label: 'Cat 9 · Downstream', note: 'Freight out', value: cat9 },
    { cat: 6, label: 'Cat 6 · Business travel', note: 'Travel & hotels', value: cat6 },
    { cat: 7, label: 'Cat 7 · Commuting', note: 'Incl. homeworking', value: cat7 },
  ];

  const glassHandlers = GLASS ? { onPointerMove: trackLight, onPointerLeave: resetLight } : {};
  const heroClass = GLASS
    ? 'liquid-glass liquid-glass--tint'
    : 'rounded-2xl border border-primary/25 bg-accent';
  const tileClass = GLASS ? 'liquid-glass' : 'rounded-2xl border border-border bg-card shadow-sm';
  const labelInk = GLASS ? 'text-foreground/75' : 'text-muted-foreground';

  return (
    <section aria-labelledby="scope3-inventory" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 id="scope3-inventory" className="text-sm font-semibold text-foreground">Scope 3 inventory</h3>
        <span className="text-xs text-muted-foreground tabular-nums">
          {(totals?.totalShipments || 0).toLocaleString()} shipments · {(scope3Summary?.entry_count || 0).toLocaleString()} travel / commute entries
        </span>
      </div>

      <div className={GLASS ? 'liquid-field' : undefined}>
        {GLASS && (
          // Static light field the glass refracts. Each glow sits roughly under its own tile.
          <div className="liquid-field__light" aria-hidden="true">
            <span style={{ left: '2%', background: 'hsl(var(--primary))' }} />
            <span style={{ left: '22%', background: palette.category(4) }} />
            <span style={{ left: '42%', background: palette.category(9) }} />
            <span style={{ left: '62%', background: palette.category(6) }} />
            <span style={{ left: '80%', background: palette.category(7) }} />
          </div>
        )}

        {/* Laptops: total spans a full row with the category split; very wide screens: five tiles in one row. */}
        <div className="relative grid grid-cols-2 gap-3 md:grid-cols-4 min-[1600px]:grid-cols-5 sm:gap-4">
          {/* Hero: total */}
          <div
            className={`col-span-2 md:col-span-4 min-[1600px]:col-span-1 flex flex-col gap-4 p-4 sm:p-5 md:flex-row md:items-center md:gap-8 min-[1600px]:flex-col min-[1600px]:items-stretch min-[1600px]:gap-0 ${heroClass}`}
            {...glassHandlers}
          >
            <div className="shrink-0">
              <p className={`text-xs font-semibold ${GLASS ? 'text-primary' : 'text-accent-foreground'}`}>Total Scope 3</p>
              <p className="mt-2 flex items-baseline gap-1.5">
                <span className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">{totalParts.value}</span>
                <span className={`text-sm ${labelInk}`}>{totalParts.unit}</span>
              </p>
              <p className={`mt-1 text-xs ${labelInk}`}>Categories 4, 6, 7 and 9</p>
            </div>

            {/* Category split: one stacked bar (2px gaps between segments) plus a direct legend */}
            <div className="min-w-0 flex-1 min-[1600px]:hidden">
              <div
                className="flex h-2.5 gap-[2px] overflow-hidden rounded-full bg-foreground/[.06]"
                role="img"
                aria-label={`Share of total: ${tiles.map((t) => `${t.label} ${fmtShare(t.value, total)}`).join(', ')}`}
              >
                {total > 0 && tiles.filter((t) => t.value > 0).map((t) => (
                  <span key={t.cat} style={{ flexGrow: t.value, background: palette.category(t.cat) }} />
                ))}
              </div>
              <ul className={`mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs ${labelInk}`}>
                {tiles.map((t) => (
                  <li key={t.cat} className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: palette.category(t.cat) }} aria-hidden="true" />
                    <span>{t.label}</span>
                    <span className="tabular-nums text-foreground/70">{fmtShare(t.value, total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {tiles.map((t) => {
            const parts = emissionsParts(t.value);
            return (
              <div key={t.cat} className={`p-4 sm:p-5 ${tileClass}`} {...glassHandlers}>
                <p className={`flex items-center gap-2 text-xs font-medium ${labelInk}`}>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: palette.category(t.cat) }} aria-hidden="true" />
                  <span className="truncate" title={t.label}>{t.label}</span>
                </p>
                <p className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-2xl font-semibold tracking-tight tabular-nums text-foreground">{parts.value}</span>
                  <span className={`text-xs ${labelInk}`}>{parts.unit}</span>
                </p>
                <p className={`mt-1 flex items-center justify-between gap-2 text-xs ${labelInk}`}>
                  <span className="truncate">{t.note}</span>
                  <span className="tabular-nums">{fmtShare(t.value, total)}</span>
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
